import { motion } from 'framer-motion';
import {
  AlertTriangle,
  Clock,
  FileX,
  TrendingDown,
  ArrowRight,
  CheckCircle2,
  Zap,
  ShieldCheck,
  TrendingUp,
  FileText,
} from 'lucide-react';

const problems = [
  {
    icon: Clock,
    title: 'Laudos que consomem horas',
    description:
      'Calcular valores preditos, buscar referências bibliográficas, redigir interpretação clínica e formatar o laudo — tudo manualmente, a cada avaliação.',
  },
  {
    icon: FileX,
    title: 'Risco de não conformidade',
    description:
      'Prontuários incompletos ou sem os 8 elementos obrigatórios do COFFITO podem gerar multas e problemas legais em auditorias.',
  },
  {
    icon: AlertTriangle,
    title: 'Agenda desorganizada',
    description:
      'Faltas sem controle, conflitos de horários e falta de notificações resultam em perda de receita e pacientes insatisfeitos.',
  },
  {
    icon: TrendingDown,
    title: 'Perda de receita invisível',
    description:
      'Sem integração entre agenda e faturamento, sessões realizadas ficam sem cobrança e pacotes vencem sem renovação.',
  },
];

const solutions = [
  {
    icon: Zap,
    title: 'Laudos em segundos',
    description:
      'Avaliações com interpretação clínica automatizada, gráficos profissionais e referências bibliográficas — gerados com 1 clique.',
    metric: '< 30s',
    metricLabel: 'por laudo completo',
  },
  {
    icon: ShieldCheck,
    title: '100% em conformidade',
    description:
      'Prontuário eletrônico com os 8 elementos COFFITO e consentimento LGPD integrado ao fluxo de trabalho.',
    metric: '100%',
    metricLabel: 'aderência COFFITO',
  },
  {
    icon: FileText,
    title: '13 avaliações clínicas',
    description:
      'TC6, TCPE, espirometria, CAT, mMRC, SGRQ, HADS e mais — cobrindo doenças respiratórias, cardiovasculares e oncológicas.',
    metric: '13',
    metricLabel: 'tipos de avaliação',
  },
  {
    icon: TrendingUp,
    title: 'Receita sob controle',
    description:
      'Cada sessão gera faturamento automaticamente. Alertas de vencimento de pacotes evitam perda de receita.',
    metric: '80%',
    metricLabel: 'menos tempo no fechamento',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const ProblemSolution = () => {
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="container mx-auto px-4">
        {/* ─── PROBLEMA ─── */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="inline-flex rounded-full bg-red-50 border border-red-200 px-3 py-1 text-sm font-medium text-red-600 mb-4"
          >
            O problema
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl font-display font-bold text-neutral-900 mb-4"
          >
            Sua clínica ainda perde{' '}
            <span className="text-red-500">horas com laudos manuais?</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg text-neutral-600"
          >
            Fisioterapeutas gastam tempo precioso calculando valores preditos,
            buscando referências e redigindo interpretações clínicas — tempo que
            deveria ser dedicado aos pacientes.
          </motion.p>
        </div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {problems.map((problem) => {
            const Icon = problem.icon;
            return (
              <motion.div
                key={problem.title}
                variants={itemVariants}
                className="relative rounded-xl border border-red-100 bg-red-50/50 p-6 hover:border-red-200 transition-colors"
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-red-100 mb-4">
                  <Icon className="h-5 w-5 text-red-500" />
                </div>
                <h3 className="text-lg font-display font-semibold text-neutral-900 mb-2">
                  {problem.title}
                </h3>
                <p className="text-sm text-neutral-600 leading-relaxed">
                  {problem.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* ─── Seta de transição ─── */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="flex justify-center mb-20"
        >
          <div className="h-14 w-14 rounded-full bg-gradient-to-br from-cyan-500 to-violet-500 flex items-center justify-center shadow-lg shadow-cyan-500/20">
            <ArrowRight className="h-6 w-6 text-white rotate-90" />
          </div>
        </motion.div>

        {/* ─── SOLUÇÃO ─── */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="inline-flex rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-sm font-medium text-emerald-600 mb-4"
          >
            A solução
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl font-display font-bold text-neutral-900 mb-4"
          >
            O RevaHub resolve tudo isso{' '}
            <span className="bg-gradient-to-r from-cyan-600 to-violet-600 bg-clip-text text-transparent">
              em uma única plataforma
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg text-neutral-600"
          >
            Avaliações com laudo automatizado, agenda inteligente, prontuário
            eletrônico e faturamento integrado — tudo para que você foque no que
            realmente importa: seus pacientes.
          </motion.p>
        </div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {solutions.map((solution) => {
            const Icon = solution.icon;
            return (
              <motion.div
                key={solution.title}
                variants={itemVariants}
                className="relative rounded-xl border border-emerald-100 bg-gradient-to-b from-white to-emerald-50/50 p-6 hover:border-emerald-200 hover:shadow-md hover:shadow-emerald-100/50 transition-all duration-300"
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 mb-4">
                  <Icon className="h-5 w-5 text-emerald-600" />
                </div>
                <h3 className="text-lg font-display font-semibold text-neutral-900 mb-2">
                  {solution.title}
                </h3>
                <p className="text-sm text-neutral-600 leading-relaxed mb-4">
                  {solution.description}
                </p>
                <div className="pt-3 border-t border-emerald-100">
                  <span className="text-2xl font-display font-bold text-emerald-600">
                    {solution.metric}
                  </span>
                  <span className="ml-2 text-xs text-neutral-500">
                    {solution.metricLabel}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSolution;
